/**
 * Juno106Panel - Interface pour le Roland Juno-106
 * Design inspiré du vrai Juno-106 avec ses couleurs caractéristiques
 */

import { themeManager } from '../themes/ThemeManager.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { JUNO106_PRESETS } from '../../state/InstrumentPresets.js';

export class Juno106Panel {
    constructor(juno106, store) {
        this.synth = juno106;
        this.store = store;
        this.panelId = 'juno106';
        this.components = {
            knobs: {},
            selects: {},
            stepButtons: [],
            toggles: {}
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('juno106');

        this.synth.setPower(isPowered);

        container.innerHTML = `
            <div class="machine juno106 ${!isPowered ? 'powered-off' : ''} ${isCollapsed ? 'collapsed' : ''}" id="juno106_machine">
                <div class="machine-title juno106-title">
                    <button class="collapse-btn" id="juno106_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    ROLAND <span class="model">JUNO-106</span>
                    <span class="brand">Polyphonic Synthesizer</span>
                    <div id="juno106_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="juno106_power">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="juno106_content">
                <div class="juno106-controls">
                    <!-- DCO Section -->
                    <div class="juno-section dco-section">
                        <div class="section-title">DCO</div>
                        <div class="controls">
                            <div class="toggle-row">
                                <div class="toggle-group" id="juno_dco_saw"></div>
                                <div class="toggle-group" id="juno_dco_pulse"></div>
                            </div>
                            <div class="knob-group" id="juno_dco_pw"></div>
                            <div class="knob-group" id="juno_dco_oct"></div>
                        </div>
                    </div>

                    <!-- Mixer Section -->
                    <div class="juno-section mixer-section">
                        <div class="section-title">MIXER</div>
                        <div class="controls">
                            <div class="knob-group" id="juno_sub"></div>
                            <div class="knob-group" id="juno_noise"></div>
                        </div>
                    </div>

                    <!-- HPF Section -->
                    <div class="juno-section hpf-section">
                        <div class="section-title">HPF</div>
                        <div class="controls">
                            <div class="knob-group" id="juno_hpf"></div>
                        </div>
                    </div>

                    <!-- VCF Section -->
                    <div class="juno-section vcf-section">
                        <div class="section-title">VCF</div>
                        <div class="controls">
                            <div class="knob-group" id="juno_cutoff"></div>
                            <div class="knob-group" id="juno_resonance"></div>
                            <div class="knob-group" id="juno_env_amt"></div>
                            <div class="knob-group" id="juno_keytrack"></div>
                        </div>
                    </div>

                    <!-- Envelope Section -->
                    <div class="juno-section env-section">
                        <div class="section-title">ENV</div>
                        <div class="controls">
                            <div class="knob-group" id="juno_env_a"></div>
                            <div class="knob-group" id="juno_env_d"></div>
                            <div class="knob-group" id="juno_env_s"></div>
                            <div class="knob-group" id="juno_env_r"></div>
                        </div>
                    </div>

                    <!-- LFO Section -->
                    <div class="juno-section lfo-section">
                        <div class="section-title">LFO</div>
                        <div class="controls">
                            <div class="knob-group" id="juno_lfo_rate"></div>
                            <div class="knob-group" id="juno_lfo_pitch"></div>
                            <div class="knob-group" id="juno_lfo_filter"></div>
                            <div class="knob-group" id="juno_lfo_pw"></div>
                        </div>
                    </div>

                    <!-- Chorus Section -->
                    <div class="juno-section chorus-section">
                        <div class="section-title">CHORUS</div>
                        <div class="controls">
                            <div id="juno_chorus_mode"></div>
                        </div>
                    </div>

                    <!-- Master Section -->
                    <div class="juno-section master-section">
                        <div class="section-title">MASTER</div>
                        <div class="controls">
                            <div class="knob-group" id="juno_volume"></div>
                        </div>
                    </div>
                </div>

                <!-- Sequencer -->
                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${[...Array(16)].map((_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">GATE</span>
                        <div class="steps" id="juno106_gates"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">NOTE</span>
                        <div class="note-row" id="juno106_notes"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createDcoControls();
            this.createMixerControls();
            this.createFilterControls();
            this.createEnvelopeControls();
            this.createLfoControls();
            this.createChorusControls();
            this.createMasterControls();
            this.createSequencer();
            this.setupPresetSelector();
            this._loadParams();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('juno106_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('juno106_power');
        const machine = document.getElementById('juno106_machine');

        powerBtn.addEventListener('click', () => {
            const newState = !this.synth.getPower();
            this.synth.setPower(newState);
            this.store.setPowerState('juno106', newState);
            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    createDcoControls() {
        // SAW toggle
        const sawContainer = document.getElementById('juno_dco_saw');
        sawContainer.innerHTML = `
            <button class="wave-toggle ${this.synth._params.dcoSaw ? 'active' : ''}" id="juno_saw_btn">
                <span class="wave-icon">⩘</span>
                <span class="wave-label">SAW</span>
            </button>
        `;
        document.getElementById('juno_saw_btn').addEventListener('click', (e) => {
            const active = !e.target.closest('button').classList.contains('active');
            e.target.closest('button').classList.toggle('active', active);
            this.synth.setDcoSaw(active);
            this._saveParams();
        });

        // PULSE toggle
        const pulseContainer = document.getElementById('juno_dco_pulse');
        pulseContainer.innerHTML = `
            <button class="wave-toggle ${this.synth._params.dcoPulse ? 'active' : ''}" id="juno_pulse_btn">
                <span class="wave-icon">⊓</span>
                <span class="wave-label">PULSE</span>
            </button>
        `;
        document.getElementById('juno_pulse_btn').addEventListener('click', (e) => {
            const active = !e.target.closest('button').classList.contains('active');
            e.target.closest('button').classList.toggle('active', active);
            this.synth.setDcoPulse(active);
            this._saveParams();
        });

        // Pulse Width
        this.components.knobs.dcoPW = themeManager.createKnob(
            document.getElementById('juno_dco_pw'), {
                min: 0.1, max: 0.9, value: this.synth._params.dcoPulseWidth,
                label: 'PW', step: 0.01
            }
        ).onChange(value => {
            this.synth.setDcoPulseWidth(value);
            this._saveParams();
        });

        // Octave
        this.components.knobs.dcoOct = themeManager.createKnob(
            document.getElementById('juno_dco_oct'), {
                min: -1, max: 1, value: this.synth._params.dcoOctave,
                label: 'RANGE', step: 1
            }
        ).onChange(value => {
            this.synth.setDcoOctave(value);
            this._saveParams();
        });
    }

    createMixerControls() {
        this.components.knobs.subLevel = themeManager.createKnob(
            document.getElementById('juno_sub'), {
                min: 0, max: 1, value: this.synth._params.subLevel,
                label: 'SUB', step: 0.01
            }
        ).onChange(value => {
            this.synth.setSubLevel(value);
            this._saveParams();
        });

        this.components.knobs.noiseLevel = themeManager.createKnob(
            document.getElementById('juno_noise'), {
                min: 0, max: 1, value: this.synth._params.noiseLevel,
                label: 'NOISE', step: 0.01
            }
        ).onChange(value => {
            this.synth.setNoiseLevel(value);
            this._saveParams();
        });
    }

    createFilterControls() {
        // HPF (simple step selector)
        this.components.knobs.hpf = themeManager.createKnob(
            document.getElementById('juno_hpf'), {
                min: 0, max: 3, value: this.synth._params.hpfFreq,
                label: 'HPF', step: 1
            }
        ).onChange(value => {
            this.synth.setHpfFreq(Math.round(value));
            this._saveParams();
        });

        this.components.knobs.cutoff = themeManager.createKnob(
            document.getElementById('juno_cutoff'), {
                min: 20, max: 20000, value: this.synth._params.lpfCutoff,
                label: 'FREQ', step: 1
            }
        ).onChange(value => {
            this.synth.setLpfCutoff(value);
            this._saveParams();
        });

        this.components.knobs.resonance = themeManager.createKnob(
            document.getElementById('juno_resonance'), {
                min: 0, max: 20, value: this.synth._params.lpfResonance,
                label: 'RES', step: 0.1
            }
        ).onChange(value => {
            this.synth.setLpfResonance(value);
            this._saveParams();
        });

        this.components.knobs.envAmt = themeManager.createKnob(
            document.getElementById('juno_env_amt'), {
                min: 0, max: 1, value: this.synth._params.lpfEnvAmount,
                label: 'ENV', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLpfEnvAmount(value);
            this._saveParams();
        });

        this.components.knobs.keyTrack = themeManager.createKnob(
            document.getElementById('juno_keytrack'), {
                min: 0, max: 1, value: this.synth._params.lpfKeyTrack,
                label: 'KYBD', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLpfKeyTrack(value);
            this._saveParams();
        });
    }

    createEnvelopeControls() {
        const envParams = [
            { id: 'juno_env_a', param: 'envAttack', label: 'A', setter: 'setEnvAttack' },
            { id: 'juno_env_d', param: 'envDecay', label: 'D', setter: 'setEnvDecay' },
            { id: 'juno_env_s', param: 'envSustain', label: 'S', setter: 'setEnvSustain' },
            { id: 'juno_env_r', param: 'envRelease', label: 'R', setter: 'setEnvRelease' }
        ];

        envParams.forEach(p => {
            const isTime = p.label !== 'S';
            this.components.knobs[p.param] = themeManager.createKnob(
                document.getElementById(p.id), {
                    min: isTime ? 0.001 : 0,
                    max: isTime ? 4 : 1,
                    value: this.synth._params[p.param],
                    label: p.label, step: 0.01
                }
            ).onChange(value => {
                this.synth[p.setter](value);
                this._saveParams();
            });
        });
    }

    createLfoControls() {
        this.components.knobs.lfoRate = themeManager.createKnob(
            document.getElementById('juno_lfo_rate'), {
                min: 0.1, max: 30, value: this.synth._params.lfoRate,
                label: 'RATE', step: 0.1
            }
        ).onChange(value => {
            this.synth.setLfoRate(value);
            this._saveParams();
        });

        this.components.knobs.lfoToPitch = themeManager.createKnob(
            document.getElementById('juno_lfo_pitch'), {
                min: 0, max: 1, value: this.synth._params.lfoToPitch,
                label: 'DCO', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToPitch(value);
            this._saveParams();
        });

        this.components.knobs.lfoToFilter = themeManager.createKnob(
            document.getElementById('juno_lfo_filter'), {
                min: 0, max: 1, value: this.synth._params.lfoToFilter,
                label: 'VCF', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToFilter(value);
            this._saveParams();
        });

        this.components.knobs.lfoToPW = themeManager.createKnob(
            document.getElementById('juno_lfo_pw'), {
                min: 0, max: 1, value: this.synth._params.lfoToPW,
                label: 'PWM', step: 0.01
            }
        ).onChange(value => {
            this.synth.setLfoToPW(value);
            this._saveParams();
        });
    }

    createChorusControls() {
        const chorusModes = [
            { value: 0, label: 'OFF' },
            { value: 1, label: 'I' },
            { value: 2, label: 'II' },
            { value: 3, label: 'I+II' }
        ];

        this.components.chorusMode = themeManager.createSelect(
            document.getElementById('juno_chorus_mode'),
            {
                options: chorusModes.map(m => ({ value: m.value.toString(), label: m.label })),
                value: this.synth._params.chorusMode.toString(),
                className: 'chorus-select'
            }
        ).onChange(value => {
            this.synth.setChorusMode(parseInt(value));
            this._saveParams();
        });
    }

    createMasterControls() {
        this.components.knobs.volume = themeManager.createKnob(
            document.getElementById('juno_volume'), {
                min: 0, max: 1, value: this.synth._params.volume,
                label: 'VOLUME', step: 0.01
            }
        ).onChange(value => {
            this.synth.setVolume(value);
            this._saveParams();
        });
    }

    createSequencer() {
        const notesContainer = document.getElementById('juno106_notes');
        const gatesContainer = document.getElementById('juno106_gates');

        // Note selectors
        for (let i = 0; i < 16; i++) {
            this.components.selects[`note_${i}`] = themeManager.createSelect(notesContainer, {
                options: this.store.noteOptions,
                value: this.store.patterns.juno106Notes ? this.store.patterns.juno106Notes[i] : 'C3',
                className: 'note-select'
            }).onChange(value => {
                this.store.setStep('juno106Notes', i, value);
            });
        }

        // Gate buttons
        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(gatesContainer, {
                index: i,
                type: 'gate',
                active: this.store.patterns.juno106Gates ? this.store.patterns.juno106Gates[i] : false
            }).onChange(active => {
                this.store.setStep('juno106Gates', i, active);
            });

            this.components.stepButtons.push(stepBtn);
        }
    }

    setupPresetSelector() {
        const container = document.getElementById('juno106_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: JUNO106_PRESETS,
            currentPreset: 'Init',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        this.synth.setParams(presetData);
        this._updateControlsFromParams();
        this._saveParams();
    }

    _updateControlsFromParams() {
        const params = this.synth.getParams();

        // Update knobs
        Object.entries(this.components.knobs).forEach(([key, knob]) => {
            if (params[key] !== undefined) {
                knob.setValue(params[key]);
            }
        });

        // Update toggles
        const sawBtn = document.getElementById('juno_saw_btn');
        const pulseBtn = document.getElementById('juno_pulse_btn');
        if (sawBtn) sawBtn.classList.toggle('active', params.dcoSaw);
        if (pulseBtn) pulseBtn.classList.toggle('active', params.dcoPulse);

        // Update chorus mode
        if (this.components.chorusMode) {
            this.components.chorusMode.setValue(params.chorusMode.toString());
        }
    }

    highlightStep(step) {
        this.components.stepButtons.forEach((btn, i) => {
            if (btn && btn.setPlaying) {
                btn.setPlaying(i === step);
            }
        });
    }

    _saveParams() {
        this.store?.setSynthParams('juno106', this.synth.getParams());
    }

    _loadParams() {
        const saved = this.store?.getSynthParams('juno106');
        if (saved) {
            this.synth.setParams(saved);
            this._updateControlsFromParams();
        }
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy?.());
        Object.values(this.components.selects).forEach(s => s?.destroy?.());
        this.components.stepButtons.forEach(b => b?.destroy?.());
    }
}
